import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Modal } from '@material-ui/core';
import {
  generateAwsClientLink,
  updateHandoutForCoach,
  fetchHandoutSignedUrl,
} from '../../services/userApiService';

const style = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
};

const CloseSvg = () => (
  <svg width='18' height='18' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z'
      fill='currentColor'
    />
  </svg>
);

const PdfSvg = () => (
  <svg
    className='w-8 h-8 text-ab-secondary'
    fill='none'
    viewBox='0 0 24 24'
    stroke='currentColor'
    strokeWidth={1.5}
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
    />
  </svg>
);

const UploadCloudSvg = () => (
  <svg
    className='w-10 h-10 text-ab-primary text-opacity-30'
    fill='none'
    viewBox='0 0 24 24'
    stroke='currentColor'
    strokeWidth={1.5}
  >
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      d='M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.782 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z'
    />
  </svg>
);

const ViewSvg = () => (
  <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      d='M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z'
    />
    <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
  </svg>
);

const UploadSvg = () => (
  <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      d='M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5'
    />
  </svg>
);

function FileUploader({ file, setFile }) {
  const handleFileSelect = (event) => {
    setFile(event.target.files[0]);
  };

  return (
    <div className='border border-dashed border-gray-c3 rounded-lg p-6 text-center hover:border-ab-secondary hover:border-opacity-40 transition-colors duration-150'>
      <UploadCloudSvg />
      <p className='text-sm font-medium text-ab-primary text-opacity-60 mt-3 mb-1'>
        Drop your PDF here or click to browse
      </p>
      <p className='text-xs text-ab-primary text-opacity-30'>PDF files only</p>
      <input
        id='handout-file'
        name='handout-file'
        type='file'
        accept='application/pdf'
        className='hidden'
        onChange={handleFileSelect}
      />
      <label
        htmlFor='handout-file'
        className='inline-block mt-3 px-4 py-1.5 border border-solid border-ab-primary border-opacity-20 text-ab-primary text-opacity-60 rounded-lg text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity duration-150'
      >
        Choose File
      </label>
      {file && (
        <div className='mt-4 flex items-center gap-3 bg-ab-primary bg-opacity-5 rounded-lg px-4 py-3'>
          <PdfSvg />
          <div className='flex-1 min-w-0'>
            <p className='text-sm font-medium text-ab-primary truncate'>{file.name}</p>
            <p className='text-xs text-ab-primary text-opacity-40'>
              {(file.size / 1024).toFixed(1)} KB
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function HandoutModal({ showModal, setShowModal, data, refreshData, handoutLinkAvailable }) {
  const openDiv = useRef(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  // handoutLinkAvailable=true → handout exists → show View/Edit (isEdit=false)
  // handoutLinkAvailable=false → no handout → show Upload form (isEdit=true)
  const [isEdit, setIsEdit] = useState(!handoutLinkAvailable);

  useEffect(() => {
    const handleClick = (e) => {
      if (!openDiv?.current?.contains(e.target)) {
        setShowModal(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [setShowModal]);

  const uploadFile = async () => {
    const apiData = {
      activityId: data?.activityId,
      fileFormat: 'pdf',
    };
    const res = await generateAwsClientLink(apiData);
    const { clientUrl, itemKey } = res;

    await axios.put(clientUrl, file, {
      headers: { 'Content-Type': 'application/pdf' },
    });

    const bodyData = {
      activityId: data?.activityId,
      handout: {
        type: 's3',
        link: itemKey,
      },
    };
    await updateHandoutForCoach(bodyData);
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a PDF file to upload.');
      return;
    }
    setIsUploading(true);
    try {
      await uploadFile();
      setShowModal(false);
      if (refreshData) await refreshData();
    } catch (error) {
      console.error('Handout upload error:', error);
      alert('Failed to upload handout. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleViewHandout = async () => {
    try {
      const res = await fetchHandoutSignedUrl(data?.classActivityRecording?.handout?.link);
      window.open(res?.signedUrl, '_blank');
    } catch (error) {
      console.error('Error fetching handout URL:', error);
    }
  };

  return (
    <Modal open={showModal} onClose={() => setShowModal(false)}>
      <Box sx={{ ...style, minHeight: 360 }}>
        <div
          ref={openDiv}
          className='bg-white md:w-2/5 sm:w-2/3 w-11/12 rounded-xl shadow-xl overflow-hidden'
        >
          {/* Header */}
          <div className='flex items-center justify-between px-6 py-4 border-b border-solid border-gray-c1'>
            <h2 className='text-base font-semibold text-ab-primary'>
              {!isEdit ? 'Handout' : 'Upload Handout'}
            </h2>
            <button
              onClick={() => setShowModal(false)}
              className='text-ab-primary text-opacity-40 hover:text-opacity-70 transition-opacity duration-150 cursor-pointer bg-transparent border-none p-0'
            >
              <CloseSvg />
            </button>
          </div>

          {/* Body */}
          <div className='px-6 py-5'>
            {!isEdit ? (
              /* ── View / Edit mode (handout exists) ── */
              <div className='flex flex-col gap-3 py-2'>
                <div className='flex items-center gap-4 p-4 bg-ab-primary bg-opacity-5 rounded-lg'>
                  <div className='w-10 h-10 rounded-lg bg-ab-secondary bg-opacity-10 flex items-center justify-center shrink-0'>
                    <svg
                      className='w-5 h-5 text-ab-secondary'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={1.5}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
                      />
                    </svg>
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-ab-primary'>Handout Ready</p>
                    <p className='text-xs text-ab-primary text-opacity-40 mt-0.5'>
                      PDF document uploaded
                    </p>
                  </div>
                </div>
                <div className='flex items-center gap-2 mt-1'>
                  <button
                    className='flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-ab-secondary text-ab-tertiary rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 hover:opacity-90 active:scale-[0.98]'
                    onClick={handleViewHandout}
                  >
                    <ViewSvg />
                    View Handout
                  </button>
                  <button
                    className='inline-flex items-center justify-center gap-1.5 px-3 py-2 text-ab-primary text-opacity-50 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150 hover:text-opacity-70 hover:bg-ab-primary hover:bg-opacity-5 active:scale-[0.98] bg-transparent'
                    onClick={() => setIsEdit(true)}
                  >
                    <UploadSvg />
                    Replace
                  </button>
                </div>
              </div>
            ) : (
              /* ── Upload mode (no handout yet) ── */
              <div className='flex flex-col gap-4'>
                <FileUploader file={file} setFile={setFile} />
                <button
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-ab-secondary text-ab-tertiary rounded-lg text-sm font-medium transition-all duration-150 ${
                    !file || isUploading
                      ? 'opacity-40 cursor-not-allowed'
                      : 'cursor-pointer hover:opacity-90 active:scale-[0.98]'
                  }`}
                  onClick={handleUpload}
                  disabled={!file || isUploading}
                >
                  {isUploading ? (
                    <>
                      <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <UploadSvg />
                      Upload Handout
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default HandoutModal;
